import { useEffect } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { compact } from "lodash/fp";

import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import { BillingCustomer } from "@app/common/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";

export const getQueryKey = (userId?: string) => compact(["billing", userId]);

const useBillingCustomer = () => {
  const queryClient = useQueryClient();
  const { user, fetchAndSetUser } = useAuth();
  const { workspaceLoading } = useWorkspace();
  const queryKey = getQueryKey(user?.id);

  const customerQuery = useQuery({
    queryKey,
    queryFn: apiClient.billing.getCustomer,
    enabled: Boolean(user?.id),
    refetchOnWindowFocus: true,
  });

  const updateCustomer = useMutation({
    mutationFn: apiClient.billing.patchCustomer,
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<BillingCustomer>(
        queryKey,
        (old: BillingCustomer | undefined) =>
          old
            ? {
                ...old,
                ...body,
              }
            : undefined
      );
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
  });

  useEffect(() => {
    if (user?.billingAccount?.stripeCustomerId) return;
    if (customerQuery.isSuccess) {
      fetchAndSetUser();
    }
  }, [customerQuery.isSuccess]);

  return {
    isLoading: workspaceLoading || customerQuery.isLoading,
    isError: customerQuery.isError,
    billingCustomer: customerQuery.data,
    updateCustomer,
  };
};

export default useBillingCustomer;
